import { useState, useEffect, useRef, useCallback } from "react";
import "./css/LocationPicker.css";

export default function LocationPicker({ isOpen, onClose, onSelect, initialLat, initialLng, lang = "en" }) {
  const [selectedLat, setSelectedLat] = useState(initialLat || 25.2048);
  const [selectedLng, setSelectedLng] = useState(initialLng || 55.2708);
  const mapRef = useRef(null);
  const autocompleteContainerRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const autocompleteElementRef = useRef(null);

  // Translations
  const t = {
    en: {
      selectLocation: "Select Location",
      searchPlace: "Search for a place...",
      continue: "Continue",
      cancel: "Cancel",
      loadingMap: "Loading map...",
      mapError: "Failed to load Google Maps. Please check your API key.",
    },
    ar: {
      selectLocation: "اختر الموقع",
      searchPlace: "ابحث عن مكان...",
      continue: "متابعة",
      cancel: "إلغاء",
      loadingMap: "جاري تحميل الخريطة...",
      mapError: "فشل تحميل خرائط Google. يرجى التحقق من مفتاح API الخاص بك.",
    },
  }[lang];

  // Update coordinates when initial values change
  useEffect(() => {
    if (initialLat && initialLng) {
      setSelectedLat(parseFloat(initialLat));
      setSelectedLng(parseFloat(initialLng));
    }
  }, [initialLat, initialLng, isOpen]);

  // Initialize map function - only depend on initial values to prevent infinite loops
  const initializeMap = useCallback(() => {
    // Prevent multiple initializations
    if (mapInstanceRef.current) {
      return;
    }

    if (!window.google || !mapRef.current) return;

    // Initialize map centered on UAE (default) or initial location
    let defaultCenter;
    if (initialLat && initialLng) {
      defaultCenter = { lat: parseFloat(initialLat), lng: parseFloat(initialLng) };
    } else {
      defaultCenter = { lat: 25.2048, lng: 55.2708 }; // UAE default
    }

    const newMap = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: initialLat && initialLng ? 15 : 13,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    mapInstanceRef.current = newMap;

    // Set initial coordinates without animation to prevent loop
    setSelectedLat(defaultCenter.lat);
    setSelectedLng(defaultCenter.lng);

    // Wait for container to be ready before creating marker and autocomplete
    const initializeMarkerAndAutocomplete = async () => {
      if (!autocompleteContainerRef.current) {
        setTimeout(initializeMarkerAndAutocomplete, 100);
        return;
      }

      // Create marker
      const newMarker = new window.google.maps.Marker({
        position: defaultCenter,
        map: newMap,
        draggable: true,
        animation: null, // Remove DROP animation to prevent loop
      });

      markerRef.current = newMarker;

      // Update coordinates when marker is dragged
      newMarker.addListener("dragend", (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setSelectedLat(lat);
        setSelectedLng(lng);
        newMap.setCenter({ lat, lng });
      });

      // Initialize PlaceAutocompleteElement (New API)
      try {
        // Import the places library
        if (!window.google || !window.google.maps) {
          setTimeout(initializeMarkerAndAutocomplete, 200);
          return;
        }

        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");

        // Clean up existing autocomplete element if any
        if (autocompleteElementRef.current && autocompleteElementRef.current.parentNode) {
          autocompleteElementRef.current.remove();
          autocompleteElementRef.current = null;
        }

        // Handle place selection - new API uses 'gmp-select' event with placePrediction
        const handlePlaceSelect = async (event) => {
          try {
            console.log("Place selected event:", event);
            
            // In the new API, event contains placePrediction which needs to be converted to Place
            let place;
            if (event.placePrediction) {
              // New API: Convert placePrediction to Place object
              place = await event.placePrediction.toPlace();
            } else if (event.place) {
              // Direct place object (fallback)
              place = event.place;
            } else if (event.detail && event.detail.placePrediction) {
              place = await event.detail.placePrediction.toPlace();
            } else if (event.detail && event.detail.place) {
              place = event.detail.place;
            } else {
              console.error("Unable to get place from event:", event);
              return;
            }
            
            console.log("Place object:", place);
            
            // Fetch fields - location is usually available but fetch to be sure
            await place.fetchFields({
              fields: ["displayName", "formattedAddress", "location", "viewport"],
            });

            // Get location coordinates
            if (!place.location) {
              console.error("Place location is not available after fetchFields");
              return;
            }

            // Get coordinates from location (LatLng object)
            const lat = place.location.lat();
            const lng = place.location.lng();
            
            console.log("Coordinates:", lat, lng);
            
            // Update state
            setSelectedLat(lat);
            setSelectedLng(lng);
            
            // Get current map and marker from refs
            const currentMarker = markerRef.current;
            const currentMap = mapInstanceRef.current;
            
            if (!currentMarker) {
              console.error("Marker ref is null - cannot update position");
              return;
            }
            
            if (!currentMap) {
              console.error("Map ref is null - cannot update view");
              return;
            }
            
            // Update marker position
            currentMarker.setPosition({ lat, lng });
            console.log("Marker moved to:", lat, lng);
            
            // Update map view - prefer viewport if available, otherwise center and zoom
            if (place.viewport) {
              currentMap.fitBounds(place.viewport);
              console.log("Map fitted to viewport");
            } else {
              currentMap.setCenter({ lat, lng });
              currentMap.setZoom(15);
              console.log("Map centered to:", lat, lng, "with zoom 15");
            }
          } catch (error) {
            console.error("Error handling place selection:", error);
            console.error("Error details:", error.message, error.stack);
          }
        };

        // Create new PlaceAutocompleteElement
        // PlaceAutocompleteElement constructor doesn't accept configuration object
        // The new API has different properties than the legacy API
        const autocompleteElement = new PlaceAutocompleteElement();
        
        // Note: Many legacy properties are not available in the new API
        // Country restrictions and types may need to be set differently or are not supported yet
        // The new API focuses on PlacePrediction rather than filtering by country in the element itself

        // Style the element to match our design
        const currentLang = localStorage.getItem("app_language") || "en";
        const placeholderText = currentLang === "ar" ? "ابحث عن مكان..." : "Search for a place...";
        
        // Set styles directly on the element - ensure it's visible
        autocompleteElement.style.cssText = `
          width: 100% !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          min-height: 44px;
        `;
        autocompleteElement.setAttribute("placeholder", placeholderText);
        autocompleteElement.className = "location-search-input";
        autocompleteElement.id = "place-autocomplete-input";
        
        // Force light theme (white background) - remove dark theme if present
        autocompleteElement.removeAttribute("theme");
        autocompleteElement.setAttribute("theme", "light");

        // Append to container first to ensure it's in the DOM
        if (autocompleteContainerRef.current) {
          autocompleteContainerRef.current.innerHTML = ""; // Clear container
          autocompleteContainerRef.current.appendChild(autocompleteElement);
          autocompleteElementRef.current = autocompleteElement;

          // Add event listener for place selection
          // Try both event names - new API might use 'gmp-select' instead of 'gmp-placeselect'
          autocompleteElement.addEventListener("gmp-select", handlePlaceSelect);
          autocompleteElement.addEventListener("gmp-placeselect", handlePlaceSelect);

          // Force a reflow to ensure the element is rendered
          void autocompleteElement.offsetHeight;

          // Try to style internal input after a small delay (shadow DOM may not be ready immediately)
          setTimeout(() => {
            if (autocompleteElement.shadowRoot) {
              const input = autocompleteElement.shadowRoot.querySelector('input');
              if (input) {
                input.style.cssText = `
                  width: 100% !important;
                  padding: 12px 16px !important;
                  border: 1px solid #d9d9d9 !important;
                  border-radius: 8px !important;
                  font-size: 15px !important;
                  outline: none !important;
                  font-family: inherit !important;
                  box-sizing: border-box !important;
                  display: block !important;
                  background-color: #ffffff !important;
                  color: #000000 !important;
                `;
              }
              
              // Also style any other elements in shadow DOM that might have dark theme
              const style = document.createElement('style');
              style.textContent = `
                input {
                  background-color: #ffffff !important;
                  color: #000000 !important;
                }
                input::placeholder {
                  color: #999999 !important;
                }
              `;
              autocompleteElement.shadowRoot.appendChild(style);
            }
          }, 100);
        } else {
          console.error("Autocomplete container ref is null");
        }
      } catch (error) {
        console.error("Error initializing PlaceAutocompleteElement:", error);
        console.warn("Make sure 'Places API (New)' is enabled in Google Cloud Console");
        
        // Fallback: Show error message in container
        if (autocompleteContainerRef.current) {
          autocompleteContainerRef.current.innerHTML = `
            <div style="padding: 12px; border: 1px solid #d9d9d9; border-radius: 8px; color: #666;">
              Place Autocomplete not available. Please check API configuration.
            </div>
          `;
        }
      }

      // Click on map to move marker
      newMap.addListener("click", (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        newMarker.setPosition({ lat, lng });
        setSelectedLat(lat);
        setSelectedLng(lng);
      });
    };

    // Initialize marker and autocomplete after a delay to ensure DOM is ready
    setTimeout(initializeMarkerAndAutocomplete, 300);
  }, [initialLat, initialLng]);

  // Load Google Maps script
  useEffect(() => {
    if (!isOpen) {
      // Reset map instance when modal closes
      mapInstanceRef.current = null;
      return;
    }

    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
    
    if (!GOOGLE_MAPS_API_KEY) {
      console.error("REACT_APP_GOOGLE_MAPS_API_KEY is not set in environment variables");
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps) {
      // Small delay to ensure DOM is ready
      setTimeout(() => initializeMap(), 200);
      return;
    }

    // Load Google Maps script
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      // Script already exists, wait for it to load if needed
      if (window.google && window.google.maps) {
        setTimeout(() => initializeMap(), 200);
      } else {
        existingScript.addEventListener("load", () => {
          setTimeout(() => initializeMap(), 200);
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => initializeMap(), 200);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps script");
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup when modal closes or component unmounts
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (autocompleteElementRef.current && autocompleteElementRef.current.parentNode) {
        try {
          autocompleteElementRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
        autocompleteElementRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, [isOpen, initializeMap]);


  const handleContinue = () => {
    if (onSelect) {
      onSelect(selectedLat, selectedLng);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="location-picker-overlay">
      <div className="location-picker-modal" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="location-picker-header">
          <h3>{t.selectLocation}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="location-picker-content">
          <div className="search-container">
            <div 
              ref={autocompleteContainerRef} 
              style={{ 
                width: "100%", 
                minHeight: "44px",
                display: "block"
              }} 
            />
          </div>
          
          <div ref={mapRef} className="map-container" />
          
          <div className="coordinates-display">
            <div>
              <strong>{lang === "ar" ? "خط العرض" : "Latitude"}:</strong> {selectedLat.toFixed(6)}
            </div>
            <div>
              <strong>{lang === "ar" ? "خط الطول" : "Longitude"}:</strong> {selectedLng.toFixed(6)}
            </div>
          </div>
        </div>
        
        <div className="location-picker-footer">
          <button className="cancel-btn" onClick={onClose}>
            {t.cancel}
          </button>
          <button className="continue-btn" onClick={handleContinue}>
            {t.continue}
          </button>
        </div>
      </div>
    </div>
  );
}

